import { en } from './en'; // Statically imported - bundled in main chunk
import type { LocaleFile } from './types';

// Vite's glob import - programmatically loads locale files at build time
const localeModules = import.meta.glob<Record<string, LocaleFile>>('./*.ts', {
  eager: false // Lazy-load for code splitting
});

/**
 * Dynamically loads a locale file by language code.
 *
 * English is statically imported and bundled in the main chunk for instant availability.
 * All other locales are dynamically imported for code-splitting and loaded on-demand.
 *
 * @param code - ISO 639-1 language code (e.g., "en", "es", "fr")
 * @returns Promise resolving to the LocaleFile object, or null if the locale is not found
 *
 * @example
 * ```typescript
 * // Load English (instant - already bundled)
 * const englishLocale = await loadLocale('en');
 *
 * // Load Spanish (lazy-loaded via dynamic import)
 * const spanishLocale = await loadLocale('es');
 *
 * // Handle unknown locale
 * const locale = await loadLocale('unknown');
 * if (!locale) {
 *   console.log('Locale not found, falling back to English');
 * }
 * ```
 */
export async function loadLocale(code: string): Promise<LocaleFile | null> {
  // Return English immediately (no async import needed)
  if (code === 'en') {
    return en;
  }

  // Programmatically load other locales using Vite's glob import
  const modulePath = `./${code}.ts`;
  const loader = localeModules[modulePath];

  if (!loader) {
    return null;
  }

  try {
    const module = await loader();
    return module[code] ?? null;
  } catch {
    // Locale file doesn't exist - return null without throwing
    return null;
  }
}

// Re-export types for convenience
export type { LocaleFile, LocaleMetadata } from './types';
